import { Typography, Box, TextField, MenuItem, Button, useMediaQuery } from '@mui/material';
import { useState, useMemo, useContext } from 'react';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  getPunchlistVendorPerformanceReport,
  getPunchlistNeighborhoodPerformanceReport,
  getPunchlistItemsReport
} from '../../../../fetch';
import {
  IResponse,
  IPunchlistItemsReport,
  IDateRange,
  IPunchlistNeighborhoodPerformance,
  IPunchlistVendorPerformance
} from '../../../../models';
import {
  Page,
  PageTitleWithButton,
  Pagination,
  Table,
  Link,
  SearchSection
} from '../../../../components';
import { formatDate, getEnvironment } from '../../../../helpers';
import { PunchlistFilters } from '../PunchlistFilters';
import { TodoDetailModal } from '../punchlist-item';
import { MobilePunchlistReport } from './MobilePunchlistReport';
import { SalesContext, update } from '../../../../context/sales';

const PAGE_NAME = 'punchlist-reports';
export const PunchlistReportsPage = () => {
  const [selectedReport, setSelectedReport] = useState<string>('items');
  const [territoryId, setTerritoryId] = useState<string | number>('');
  const [selectedLotNumber, setSelectedLotNumber] = useState<string>('');
  const [selectedCount, setSelectedCount] = useState<number>(1);
  const [neighborhoodId, setNeighborhoodId] = useState<string | number>('');
  const [vendorId, setVendorId] = useState<string | number>('');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    lotNumber?: string;
    vendorId?: number | string;
    startDate?: string;
    endDate?: string;
    territoryId?: string | number;
    isPastDue?: boolean;
    itemCount?: number;
  } | null>({});
  const [page, setPage] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(() => currentPageState ?? 10, [currentPageState]);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [selectedSort, setSelectedSort] = useState<string>('Territory');
  const [sortDirection, setSortDirection] = useState<{
    Territory?: 'Asc' | 'Desc';
    Neighborhood?: 'Asc' | 'Desc';
    Vendor?: 'Asc' | 'Desc';
    VendorName?: 'Asc' | 'Desc';
    Description?: 'Asc' | 'Desc';
    DueDate?: 'Asc' | 'Desc';
    ItemCount?: 'Asc' | 'Desc';
  }>({
    Territory: 'Desc'
  });
  const [isTodoModalOpen, showTodoModal] = useState<boolean>(false);
  const [currentTodoId, setCurrentTodoId] = useState<number | null>(null);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  // context
  const { enqueueSnackbar } = useSnackbar();
  const isSmMobile = useMediaQuery('(max-width: 600px)');
  // queries
  const {
    isLoading: isLoadingItemsReport,
    data: itemsReportData,
    refetch: refetchPunchlistItemsReport
  } = useQuery<IResponse<IPunchlistItemsReport[]>, Error>(
    [
      'items-report',
      page,
      perPage,
      filters,
      selectedReport,
      searchedValue,
      selectedSort,
      sortDirection
    ],
    () =>
      getPunchlistItemsReport({
        page: page + 1,
        perPage,
        description: searchedValue,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      }),

    {
      onSuccess: (d: any) => {
        const data: IResponse<IPunchlistItemsReport[]> = d;
        setRecordCount(data.totalRecordCount);
        return d;
      },
      onError: (e: any) => {
        // make sure the call doesn't refresh again if we hit an error
        enqueueSnackbar(e?.Detail || `Error loading items report.`, {
          variant: 'error'
        });
      },
      enabled: selectedReport === 'items',
      notifyOnChangeProps: 'tracked'
    }
  );
  const { isLoading: isLoadingNeighborhoodReport, data: neighborhoodReportData } = useQuery<
    IPunchlistNeighborhoodPerformance[],
    Error
  >(
    ['neighborhood-report', filters, selectedReport, selectedSort, sortDirection],
    () =>
      getPunchlistNeighborhoodPerformanceReport({
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      }),

    {
      onError: (e: any) => {
        // make sure the call doesn't refresh again if we hit an error
        enqueueSnackbar(e?.Detail || `Error loading neighborhood report.`, {
          variant: 'error'
        });
      },
      enabled: selectedReport === 'neighborhood',
      notifyOnChangeProps: 'tracked'
    }
  );
  const { isLoading: isLoadingVendorReport, data: vendorReportData } = useQuery<
    IPunchlistVendorPerformance[],
    Error
  >(
    ['vendor-report', filters, selectedReport, selectedSort, sortDirection],
    () =>
      getPunchlistVendorPerformanceReport({
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      }),

    {
      onError: (e: any) => {
        // make sure the call doesn't refresh again if we hit an error
        enqueueSnackbar(e?.Detail || `Error loading vendor report.`, {
          variant: 'error'
        });
      },
      enabled: selectedReport === 'vendor',
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };
  const handleResetFilters = () => {
    setFilters({
      neighborhoodId: '',
      vendorId: '',
      territoryId: '',
      itemCount: undefined,
      startDate: undefined,
      endDate: undefined,
      lotNumber: ''
    });
    setTerritoryId('');
    setNeighborhoodId('');
    setVendorId('');
    setSelectedDateRange({
      startDate: null,
      endDate: null,
      key: 'selection',
      inputValue: ''
    });
    setSearchValue('');
    setSearchedValue('');
    setHasAppliedFilters(false);
    setSelectedCount(1);
    setSelectedLotNumber('');
  };

  // initialize the data
  const itemsReport = useMemo(() => itemsReportData?.records ?? [], [itemsReportData]);
  const neighborhoodReport = useMemo(() => neighborhoodReportData ?? [], [neighborhoodReportData]);
  const vendorReport = useMemo(() => vendorReportData ?? [], [vendorReportData]);
  const tableDataColumns = useMemo(() => {
    return [
      selectedReport !== 'vendor' && {
        Header: 'Territory',
        accessor: 'territory',
        isServerSorted: selectedSort === 'Territory',
        isServerSortedDesc: sortDirection.Territory === 'Desc',
        handleClickColumn: () => handleClickColumn('Territory')
      },
      selectedReport !== 'vendor' && {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        isServerSorted: selectedSort === 'Neighborhood',
        isServerSortedDesc: sortDirection.Neighborhood === 'Desc',
        handleClickColumn: () => handleClickColumn('Neighborhood')
      },
      selectedReport !== 'neighborhood' && {
        Header: 'Vendor',
        accessor: 'vendorName',
        isServerSorted: selectedSort === 'Vendor' || selectedSort === 'VendorName',
        isServerSortedDesc: sortDirection.Vendor === 'Desc' || sortDirection.VendorName === 'Desc',
        handleClickColumn: () =>
          handleClickColumn(selectedReport === 'items' ? 'Vendor' : 'VendorName')
      },
      selectedReport === 'items' && {
        Header: 'Title',
        accessor: 'title',
        isServerSorted: selectedSort === 'Description',
        isServerSortedDesc: sortDirection.Description === 'Desc',
        handleClickColumn: () => handleClickColumn('Description'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original } };
        }) => {
          return (
            <Button
              sx={{ textAlign: 'left', textTransform: 'none' }}
              onClick={() => {
                setCurrentTodoId(original.punchListItemId);
                showTodoModal(true);
              }}
            >
              {original.title}
            </Button>
          );
        }
      },
      selectedReport === 'items' && {
        Header: 'Due Date',
        accessor: 'dueDate',
        isServerSorted: selectedSort === 'DueDate',
        isServerSortedDesc: sortDirection.DueDate === 'Desc',
        handleClickColumn: () => handleClickColumn('DueDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original } };
        }) => {
          return <>{formatDate(original.dueDate)}</>;
        }
      },
      selectedReport !== 'items' && {
        Header: 'Item Count',
        accessor: 'itemCount',
        isServerSorted: selectedSort === 'ItemCount',
        isServerSortedDesc: sortDirection.ItemCount === 'Desc',
        handleClickColumn: () => handleClickColumn('ItemCount'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original } };
        }) => {
          const params = new URLSearchParams();
          if (selectedReport === 'neighborhood') {
            params.append('neighborhoodId', original.neighborhoodId);
          } else {
            params.append('vendorId', `${original.vendorId}`);
          }
          return (
            <Link to={`/production/punchlists/todos?${params.toString()}`}>
              {original.itemCount}
            </Link>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, selectedSort, sortDirection]);
  return (
    <Page title="Punchlist Reports">
      <PageTitleWithButton
        pageTitleProps={{
          title: 'Punchlist Reports'
        }}
        button={
          <Box display="flex" alignItems="center">
            <Typography sx={{ marginRight: '8px' }}>Select Report:</Typography>
            <TextField
              label={'Reports'}
              name={`reports`}
              id={`reports`}
              size="small"
              value={selectedReport}
              select
              onChange={event => {
                setSelectedReport(event.target.value);
                handleResetFilters();
                setSelectedSort(event.target.value !== 'items' ? 'ItemCount' : 'Territory');
                setSortDirection(
                  event.target.value !== 'items'
                    ? {
                        ItemCount: 'Desc'
                      }
                    : {
                        Territory: 'Asc'
                      }
                );
              }}
            >
              {[
                { label: 'Past Due Punchlist Items', value: 'items' },
                { label: 'Punchlist Performance by Neighborhood', value: 'neighborhood' },
                { label: 'Punchlist Performance by Vendor', value: 'vendor' }
              ].map((report, index) => {
                return (
                  <MenuItem key={`${index}`} value={report.value}>
                    {report.label}
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>
        }
      />
      <PunchlistFilters
        isLoading={isLoadingItemsReport}
        selectedTerritoryId={territoryId}
        setSelectedTerritoryId={setTerritoryId}
        selectedNeighborhoodId={neighborhoodId}
        selectedLotNumber={selectedLotNumber}
        setSelectedLotNumber={setSelectedLotNumber}
        selectedVendorId={vendorId}
        setSelectedVendorId={setVendorId}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={
          (selectedReport === 'items' || selectedReport === 'vendor') && setSelectedDateRange
        }
        setSelectedNeighborhoodId={setNeighborhoodId}
        setHasAppliedFilters={setHasAppliedFilters}
        hasAppliedFilters={hasAppliedFilters}
        setSearchValue={setSearchValue}
        setSearchedValue={setSearchedValue}
        searchValue={searchValue}
        searchPlaceholder="Search by title..."
        setSelectedCount={selectedReport !== 'items' && setSelectedCount}
        selectedCount={selectedCount}
        handleSearch={
          selectedReport === 'items'
            ? (val: string) => {
                setPage(0);
                setSearchedValue(val);
              }
            : undefined
        }
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            handleResetFilters();
          } else {
            setPage(0);
            setFilters({
              ...filters,
              neighborhoodId: neighborhoodId || undefined,
              vendorId: vendorId || undefined,
              territoryId: territoryId || undefined,
              itemCount: selectedCount ?? undefined,
              startDate: selectedDateRange?.startDate?.toISOString() || undefined,
              endDate: selectedDateRange?.endDate?.toISOString() || undefined,
              lotNumber: selectedLotNumber || undefined
            });
          }
        }}
      />
      {searchedValue && (
        <SearchSection
          searchedValue={searchedValue}
          handleClearSearch={() => {
            setSearchValue('');
            setSearchedValue('');
            setHasAppliedFilters(false);
            setPage(0);
          }}
        />
      )}
      <Table
        mobileProps={{
          handleTitleClick: original => {
            setCurrentTodoId(original.punchListItemId);
            showTodoModal(true);
          },
          selectedReport
        }}
        ResponsiveComponent={isSmMobile ? MobilePunchlistReport : undefined}
        columns={tableDataColumns}
        data={
          selectedReport === 'items'
            ? itemsReport
            : selectedReport === 'neighborhood'
            ? neighborhoodReport
            : vendorReport
        }
        isLoading={isLoadingItemsReport || isLoadingNeighborhoodReport || isLoadingVendorReport}
        hidePagination
      />
      {!isLoadingItemsReport && itemsReport?.length > 0 && selectedReport === 'items' && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={itemsPerPage => {
            return dispatchPerPage(
              update({
                pageName: PAGE_NAME,
                environment: getEnvironment(),
                itemsPerPage: itemsPerPage
              })
            );
          }}
        />
      )}
      <TodoDetailModal
        open={isTodoModalOpen}
        punchlistItemId={currentTodoId}
        handleClose={(shouldRefresh, shouldClose = true) => {
          if (shouldClose) showTodoModal(false);
          setCurrentTodoId(null);
          if (shouldRefresh) {
            refetchPunchlistItemsReport();
          }
        }}
      />
    </Page>
  );
};
